import Dialog from '@pelckmans/elementis/components/dialog';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../../contexts/ModalContext';
import { fetchSmartschoolMembers } from '../../api/MyGroupsApi';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import { SchoolYear } from '../../context/MyGroupsContext';
import Tabs from '@pelckmans/elementis/components/tabs';
import Badge from '@pelckmans/elementis/components/badge';
import InfoContent from './components/InfoContent';
import InfoFooter from './components/InfoFooter';
import StudentsContent from './components/StudentsContent';
import StudentsFooter from './components/StudentsFooter';
import { fetchMembers, Group, Member } from '../../../../api/groups';

const FORM_ID = 'group-details-form';

const TABS = {
  INFO: 'INFO',
  STUDENTS: 'STUDENTS',
} as const;

type TabType = typeof TABS[keyof typeof TABS];

type Props = {
  id: string;
  group: Group;
  schoolYear: SchoolYear;
  editable: boolean;
  onGroupRename: (groupWithNewName: Group) => void;
  onGroupDelete: (deletedGroup: Group) => void;
};

function GroupDetailsModal({ id, group, schoolYear, editable, onGroupRename, onGroupDelete }: Props) {
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();
  const [tab, setTab] = useState<TabType>(TABS.INFO);

  const { closeModal } = useContext(ModalContext);

  const handleClose = () => {
    closeModal(id);
  };

  const [members, setMembers] = useState<Member[]>([]);

  useEffect(() => {
    const getMembers = async () => {
      const isSmartschool = group.school.isSmartschool;

      const groupMembers = isSmartschool
        ? await fetchSmartschoolMembers(schoolYear, group.id)
        : await fetchMembers(schoolYear, group.id);

      setMembers(groupMembers);
    };

    getMembers().catch(handleError);
  }, [schoolYear, group.id, handleError, group.school.isSmartschool]);

  return (
    <Dialog
      id={id}
      modal
      size="sm"
      onClose={handleClose}
      aria-label={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.A11Y_LABEL')}
    >
      <Tabs onTabChange={tab => setTab(tab as TabType)}>
        <Dialog.Header
          title={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.HEADER.TITLE', { groupName: group.name })}
          onClose={handleClose}
          aria-label={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.HEADER.ACTIONS.CLOSE')}
        >
          <Tabs.TabList aria-label="tab-list" fluid>
            <Tabs.Tab id={TABS.INFO}>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.TITLE')}</Tabs.Tab>
            <Tabs.Tab
              id={TABS.STUDENTS}
              badge={<Badge>{group.totalAccepted + group.totalPending + group.totalDeclined}</Badge>}
            >
              {t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.TITLE')}
            </Tabs.Tab>
          </Tabs.TabList>
        </Dialog.Header>
        <Dialog.Body>
          <Tabs.TabsContent>
            <Tabs.TabPanel id={TABS.INFO}>
              <InfoContent
                group={group}
                editable={editable}
                schoolYear={schoolYear}
                onGroupRename={onGroupRename}
                onClose={handleClose}
                formId={FORM_ID}
              />
            </Tabs.TabPanel>
            <Tabs.TabPanel id={TABS.STUDENTS}>
              <StudentsContent group={group} members={members} />
            </Tabs.TabPanel>
          </Tabs.TabsContent>
        </Dialog.Body>
        {tab === 'INFO' && (
          <InfoFooter
            group={group}
            schoolYear={schoolYear}
            editable={editable}
            onGroupDelete={onGroupDelete}
            onClose={handleClose}
            formId={FORM_ID}
          />
        )}
        {tab === 'STUDENTS' && <StudentsFooter onClose={handleClose} />}
      </Tabs>
    </Dialog>
  );
}

export default GroupDetailsModal;
